"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/components/ui/alert-dialog";
import { Button } from "@/app/components/ui/button";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  durationMs?: number;
}

const LiveCloseTransitionDialog = ({ open, onOpenChange, onConfirm, durationMs = 5000 }: Props) => {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const onConfirmRef = useRef(onConfirm);
  const timeoutRef = useRef<number | null>(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    onConfirmRef.current = onConfirm;
  }, [onConfirm]);

  const clearRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const stopCountdown = useCallback(() => {
    clearRaf();
    startTimeRef.current = null;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    hasFiredRef.current = false;
    setProgress(0);
  }, [clearRaf]);

  const startCountdown = useCallback(() => {
    setProgress(0);
    startTimeRef.current = performance.now();
    hasFiredRef.current = false;
    const tick = () => {
      if (startTimeRef.current == null) return;
      const elapsed = performance.now() - startTimeRef.current;
      const nextProgress = Math.min(1, elapsed / durationMs);
      setProgress(nextProgress);
      if (elapsed >= durationMs) {
        if (!hasFiredRef.current) {
          hasFiredRef.current = true;
          clearRaf();
          if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          onConfirmRef.current?.();
        }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    timeoutRef.current = window.setTimeout(() => {
      if (!hasFiredRef.current) {
        hasFiredRef.current = true;
        clearRaf();
        onConfirmRef.current?.();
      }
    }, durationMs);
  }, [durationMs, clearRaf]);

  useEffect(() => {
    if (open) {
      startCountdown();
    } else {
      stopCountdown();
    }
    return () => {
      clearRaf();
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [open, startCountdown, stopCountdown, clearRaf]);

  const remainingSeconds = Math.max(0, Math.ceil((1 - progress) * (durationMs / 1000)));

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) return; // prevent closing via overlay/ESC
        onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(e: KeyboardEvent) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Live close has begun</AlertDialogTitle>
          <AlertDialogDescription>
            The auction is transitioning to the live close interface. Your page will switch automatically in a moment.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mt-2">
          <Button
            variant="outline"
            className="relative overflow-hidden select-none pointer-events-none w-full"
          >
            <span
              aria-hidden
              className="absolute inset-0 origin-left will-change-transform pointer-events-none transform-gpu bg-primary/20 dark:bg-primary/25"
              style={{ transform: `scaleX(${progress})` }}
            />
            <span className="relative z-[1] flex items-center justify-center gap-2 w-full">
              Switching in {remainingSeconds}s
            </span>
          </Button>
        </div>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={() => {
              hasFiredRef.current = true;
              stopCountdown();
              onConfirm();
            }}
          >
            Switch now
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default LiveCloseTransitionDialog;


